'use client';

import { isMobileDevice } from '@/helpers/mobile-redirect';
import { trackEvent } from '@/lib/track';
import {
  bridgeScope,
  copyScopeFiles,
  draftScope,
  sweepOldFiles,
} from '@/helpers/toolBridgeFiles';

/**
 * Free tool -> widget bridge, website side.
 *
 * A free tool hands the visitor's work to the matching widget editor so they can
 * carry on where they left off and put it on their site. Both pages are served
 * from www.commoninja.com, so the handover happens entirely in the browser:
 * the tool writes the work into localStorage under a one-time key and opens
 * `/<widget-slug>/editor/content?bridge=<key>`. No server, no API, no draft
 * records to clean up.
 *
 * The widget side of this contract lives in CommonNinja/apps at
 * `packages/apps/<widget>/src/bridge/`. THE TWO MUST STAY IN STEP: the four
 * constants below are duplicated there on purpose, because they are a contract
 * between two repos rather than shared code. Change the shape, bump the version.
 *
 * Adding the next tool/widget pair should mean one call to
 * `createBridgeClickHandler` and nothing else on this side. The mapping from a
 * tool's shape onto a widget's schema deliberately lives in the widget package,
 * so a widget schema change and its mapping land in the same pull request.
 */

export const TOOL_BRIDGE_QUERY_PARAM = 'bridge';
export const TOOL_BRIDGE_STORAGE_PREFIX = 'cn:tool-bridge:';
export const TOOL_BRIDGE_VERSION = 1;

/**
 * A payload older than this is ignored by the editor and swept from storage.
 * The trip is a single navigation, so half an hour is already generous. The
 * point is that an abandoned payload can never come back days later and
 * overwrite a widget somebody has started fresh.
 */
export const TOOL_BRIDGE_TTL_MS = 30 * 60 * 1000;

export interface IToolBridgeEnvelope<TPayload> {
  v: number;
  /** Free tool slug, e.g. 'tier-list-maker'. Analytics only. */
  tool: string;
  /** Widget slug the payload is meant for, e.g. 'tier-list'. */
  widget: string;
  createdAt: number;
  payload: TPayload;
}

/**
 * Reading or writing localStorage can throw outright, not just return null:
 * private windows, blocked third-party cookies and hardened privacy settings
 * all do it. Every call here is guarded, and every failure is silent by design.
 */
function canUseStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (err) {
    return false;
  }
}

/** One-time key. Never a fixed name, so two tabs can never collide. */
function makeBridgeKey(): string {
  const random = () => Math.random().toString(36).slice(2, 10);
  return `${random()}${random()}`.replace(/[^a-z0-9]/gi, '').slice(0, 24);
}

/**
 * Clears anything this browser was left holding: expired payloads, and any
 * payload at all older than the TTL. Cheap, and it runs on every write, so a
 * visitor who abandons the trip does not accumulate litter.
 */
function sweepExpiredPayloads() {
  if (!canUseStorage()) return;

  try {
    const now = Date.now();
    const stale: string[] = [];

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(TOOL_BRIDGE_STORAGE_PREFIX)) continue;

      try {
        const envelope = JSON.parse(window.localStorage.getItem(key) || '{}');
        if (typeof envelope.createdAt !== 'number' || now - envelope.createdAt > TOOL_BRIDGE_TTL_MS) {
          stale.push(key);
        }
      } catch (err) {
        stale.push(key);
      }
    }

    stale.forEach((key) => window.localStorage.removeItem(key));
  } catch (err) {
    /* sweeping is housekeeping, never worth failing a click over */
  }
}

/**
 * `/tier-list/editor/content`, optionally carrying the one-time key.
 *
 * `extraParams` lets ONE tool add a flag its own widget understands, without
 * every other tool paying for it. The Printable Bracket Maker uses it for
 * `hideQuickStart`, because the Bracket widget opens a "choose a bracket type"
 * popup on every brand-new widget, which lands on top of a draw that has just
 * been handed over and overwrites it if the visitor completes it.
 */
export function buildEditorUrl(
  widgetSlug: string,
  bridgeKey?: string,
  extraParams?: Record<string, string>
): string {
  const base = `/${widgetSlug}/editor/content`;
  const params = new URLSearchParams();
  if (bridgeKey) params.set(TOOL_BRIDGE_QUERY_PARAM, bridgeKey);
  Object.entries(extraParams || {}).forEach(([k, v]) => params.set(k, v));
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Writes the payload and returns the editor URL to send the visitor to. If
 * storage is unavailable the visitor still gets the editor, just without their
 * work pre-filled, which is the same place the old plain CTA sent them.
 *
 * A `null` or `undefined` payload means the tool decided there is nothing safe
 * to hand over (a calculator with a broken formula reference). Nothing is
 * stored, no `?bridge=` key is added, and `handedOver` is false. Before
 * 2026-10-06 this stored an envelope holding `null` and reported a successful
 * handover, which inflated the calculator handover numbers.
 */
export function startWidgetBridge<TPayload>({
  toolSlug,
  widgetSlug,
  payload,
  extraParams,
}: {
  toolSlug: string;
  widgetSlug: string;
  payload: TPayload;
  extraParams?: Record<string, string>;
}): { url: string; handedOver: boolean; bridgeKey: string; noPayload?: boolean } {
  if (payload === null || payload === undefined) {
    return {
      url: buildEditorUrl(widgetSlug, undefined, extraParams),
      handedOver: false,
      bridgeKey: '',
      noPayload: true,
    };
  }

  if (!canUseStorage()) {
    return {
      url: buildEditorUrl(widgetSlug, undefined, extraParams),
      handedOver: false,
      bridgeKey: '',
    };
  }

  try {
    sweepExpiredPayloads();

    const key = makeBridgeKey();
    const envelope: IToolBridgeEnvelope<TPayload> = {
      v: TOOL_BRIDGE_VERSION,
      tool: toolSlug,
      widget: widgetSlug,
      createdAt: Date.now(),
      payload,
    };

    window.localStorage.setItem(
      `${TOOL_BRIDGE_STORAGE_PREFIX}${key}`,
      JSON.stringify(envelope)
    );

    return {
      url: buildEditorUrl(widgetSlug, key, extraParams),
      handedOver: true,
      bridgeKey: key,
    };
  } catch (err) {
    return {
      url: buildEditorUrl(widgetSlug, undefined, extraParams),
      handedOver: false,
      bridgeKey: '',
    };
  }
}

/**
 * The click handler every bridged tool uses.
 *
 * `getPayload` is called at click time, not at render time, so it always
 * captures what is on screen right now.
 *
 * Mobile keeps the existing behaviour of every free tool CTA - phones go to
 * signup first, because the editor is not usable on a small screen - except the
 * bridged URL is what they are forwarded to afterwards, and the payload is
 * already in storage waiting for them.
 */
export function createBridgeClickHandler<TPayload>({
  toolSlug,
  widgetSlug,
  placement,
  getPayload,
  carryFiles = false,
  extraParams,
  analytics,
}: {
  toolSlug: string;
  widgetSlug: string;
  placement: string;
  getPayload: () => TPayload;
  /**
   * Extra query parameters for the editor URL, for a flag only this tool's
   * widget understands. Left out by every tool that does not need one.
   */
  extraParams?: Record<string, string>;
  /**
   * Also carry the tool's image files across, by snapshotting whatever the tool
   * has been keeping in its draft file scope. Off by default: a tool with no
   * images should not pay for an IndexedDB round trip on click.
   */
  carryFiles?: boolean;
  /**
   * Extra properties for the `tool_handover_click` event, e.g. `{ layout:
   * 'editor-v1' }` from the shared free-tool editor. Never overrides the core
   * fields above.
   */
  analytics?: Record<string, string | number | boolean>;
}) {
  return async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();

    const { url, handedOver, bridgeKey, noPayload } = startWidgetBridge({
      toolSlug,
      widgetSlug,
      payload: getPayload(),
      extraParams,
    });

    // The snapshot has to finish BEFORE we navigate, or the editor arrives to an
    // empty store. It is a local copy of files already on this device, so it is
    // fast, but a very large board could add a beat. Failure is non-fatal: the
    // board still crosses, the pictures just arrive as placeholders as before.
    let filesCarried = 0;
    if (carryFiles && handedOver && bridgeKey) {
      try {
        filesCarried = await copyScopeFiles(
          draftScope(toolSlug),
          bridgeScope(bridgeKey)
        );
        void sweepOldFiles();
      } catch (err) {
        filesCarried = 0;
      }
    }

    try {
      // Named for the handover specifically, NOT `tool_bridge_click`. That name
      // is still fired inline by ~75 plain CTA banners, where the visitor gets a
      // link to an empty editor and nothing travels. Sharing one name between
      // the two made "did the bridge work" unanswerable without filtering on
      // properties. Now the event name alone says which happened: this one only
      // ever fires from a tool that carries the visitor's work across.
      trackEvent('tool_handover_click', {
        ...analytics,
        tool: toolSlug,
        target: `${widgetSlug}-editor`,
        placement,
        mode: 'prefill',
        // False means this visitor lands in an empty editor: either the browser
        // refused storage, or (`no_payload`) the tool had nothing safe to send.
        handed_over: handedOver,
        files_carried: filesCarried,
        ...(noPayload ? { no_payload: true } : {}),
      });
    } catch (err) {
      /* analytics must never block the click */
    }

    if (isMobileDevice()) {
      window.location.href = `/signup?redirectUrl=${encodeURIComponent(url)}`;
      return;
    }

    window.location.href = url;
  };
}

/* -------------------------------------------------------------------------- */
/*  Tool-side drafts                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Separate from the bridge, and deliberately so: this is the tool remembering
 * the visitor's work so a refresh or a stray back-button does not wipe it. It
 * also means that if a handover ever misses, the work is still sitting on the
 * tool page rather than gone.
 */
const TOOL_DRAFT_STORAGE_PREFIX = 'cn:tool-draft:';

/** Long enough to come back tomorrow, short enough not to haunt anyone. */
export const TOOL_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface IToolDraftEnvelope<TDraft> {
  v: number;
  createdAt: number;
  draft: TDraft;
}

export function saveToolDraft<TDraft>(toolSlug: string, draft: TDraft) {
  if (!canUseStorage()) return;

  try {
    const envelope: IToolDraftEnvelope<TDraft> = {
      v: TOOL_BRIDGE_VERSION,
      createdAt: Date.now(),
      draft,
    };
    window.localStorage.setItem(
      `${TOOL_DRAFT_STORAGE_PREFIX}${toolSlug}`,
      JSON.stringify(envelope)
    );
  } catch (err) {
    /* a full or blocked storage must not break the tool */
  }
}

export function loadToolDraft<TDraft>(toolSlug: string): TDraft | null {
  if (!canUseStorage()) return null;

  try {
    const raw = window.localStorage.getItem(`${TOOL_DRAFT_STORAGE_PREFIX}${toolSlug}`);
    if (!raw) return null;

    const envelope: IToolDraftEnvelope<TDraft> = JSON.parse(raw);

    if (
      !envelope ||
      envelope.v !== TOOL_BRIDGE_VERSION ||
      typeof envelope.createdAt !== 'number' ||
      Date.now() - envelope.createdAt > TOOL_DRAFT_TTL_MS ||
      !envelope.draft
    ) {
      clearToolDraft(toolSlug);
      return null;
    }

    return envelope.draft;
  } catch (err) {
    return null;
  }
}

export function clearToolDraft(toolSlug: string) {
  if (!canUseStorage()) return;

  try {
    window.localStorage.removeItem(`${TOOL_DRAFT_STORAGE_PREFIX}${toolSlug}`);
  } catch (err) {
    /* nothing to do */
  }
}
